import { memo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useBuildDuration } from 'src/components/pipelines/hooks/use-build-duration';
import { useBuildFailureRate } from 'src/components/pipelines/hooks/use-build-failure-rate';
import { useDeploymentFailureRate } from 'src/components/pipelines/hooks/use-deployment-failure-rate';
import { useDeploymentFrequency } from 'src/components/pipelines/hooks/use-deployment-frequency';
import { isJiraIntegrationErrorAbsent } from 'src/components/pipelines/hooks/use-pipelines-timeseries';
import { useTestFailureRate } from 'src/components/pipelines/hooks/use-test-failure-rate';
import { getDeploymentDashboardAsSelectOptions } from 'src/components/pipelines/redux/selectors/deployments';
import { ResizeProps, SidebarContent, Cards } from 'src/components/sidebar';
import { useIntl } from 'src/hooks/intl';
import { updateSidebarState } from 'src/redux/sidebar';
import {
  getPipelinesListSidebarWidth,
  isPipelineListSidebarOpen,
} from 'src/redux/sidebar/reducer';
import Sidebar from 'src/sections/global/containers/sidebar';
import SidebarKeyboardShortcuts from 'src/sections/repository/components/sidebar-keyboard-shortcuts';
import { getIsMobileHeaderActive } from 'src/selectors/global-selectors';
import { getCurrentRepositoryUuid } from 'src/selectors/repository-selectors';

import { AverageBuildDuration } from './charts/average-build-duration';
import { AverageBuildFailureRate } from './charts/average-build-failure-rate';
import { AverageDeploymentFailureRate } from './charts/average-deployment-failure-rate';
import { AverageDeploymentFrequency } from './charts/average-deployment-frequency';
import { AverageTestFailureRate } from './charts/average-test-failure-rate';
import { messages } from './metric-cards/i18n';
import { NoJiraAssociationInfoCard } from './metric-cards/no-jira-association-info-card';
import { MetricsGroupLabel } from './metrics-group-label';
import { MetricsGroupSelectLabel } from './metrics-group-select-label';

const SIDEBAR_TYPE = 'pipeline-list';
export type Props = {};
type EnvironmentOption = {
  label: string;
  value: string;
  uuid: string;
  type: string;
};

const BasePipelineListSidebar = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [environment, setEnvironment] = useState<EnvironmentOption>();
  const isMobileHeaderActive = useSelector(getIsMobileHeaderActive);
  const isSidebarOpen = useSelector(isPipelineListSidebarOpen);
  const expandedWidth = useSelector(getPipelinesListSidebarWidth);
  const repositoryUuid = useSelector(getCurrentRepositoryUuid);
  const environments = useSelector(getDeploymentDashboardAsSelectOptions);
  const isCollapsed = isMobileHeaderActive ? false : !isSidebarOpen;
  const onResize = (resizeProps: ResizeProps) =>
    dispatch(
      updateSidebarState({
        isOpen: !resizeProps.isCollapsed,
        width: resizeProps.width,
        sidebarType: SIDEBAR_TYPE,
      })
    );

  const toggleSidebar = () =>
    dispatch(updateSidebarState({ sidebarType: SIDEBAR_TYPE }));

  const onEnvironmentChange = (item: any) => setEnvironment(item);

  const buildDuration = useBuildDuration(repositoryUuid);
  const buildFailureRate = useBuildFailureRate(repositoryUuid);
  const testFailureRate = useTestFailureRate(repositoryUuid);
  const deploymentFrequency = useDeploymentFrequency(
    repositoryUuid,
    environment?.uuid
  );
  const deploymentFailureRate = useDeploymentFailureRate(
    repositoryUuid,
    environment?.uuid
  );

  const isJiraConnected = isJiraIntegrationErrorAbsent([
    buildDuration,
    buildFailureRate,
    testFailureRate,
    deploymentFrequency,
    deploymentFailureRate,
  ]);

  const noJiraContent: SidebarContent[] = [
    {
      type: Cards.PipelinesMetricsGroupLabel,
      content: <NoJiraAssociationInfoCard />,
    },
  ];

  const expandedContent: SidebarContent[] = [
    {
      type: Cards.PipelinesMetricsGroupLabel,
      content: (
        <MetricsGroupLabel
          label={formatMessage(messages.buildMetrics)}
          data-testid="build-metrics-label"
        />
      ),
    },
    {
      type: Cards.PipelinesMetricCard,
      content: <AverageBuildDuration {...buildDuration} />,
    },
    {
      type: Cards.PipelinesMetricCard,
      content: <AverageBuildFailureRate {...buildFailureRate} />,
    },
    {
      type: Cards.PipelinesMetricsGroupLabel,
      content: (
        <MetricsGroupLabel
          label={formatMessage(messages.testMetrics)}
          data-testid="test-metrics-label"
        />
      ),
    },
    {
      type: Cards.PipelinesMetricCard,
      content: <AverageTestFailureRate {...testFailureRate} />,
    },
    {
      type: Cards.PipelinesMetricsGroupSelectLabel,
      content: (
        <MetricsGroupSelectLabel
          options={environments.filter(e => e.label !== 'Other')}
          onOptionSelect={onEnvironmentChange}
          value={environment}
          placeholder={formatMessage(messages.environmentPlaceholder)}
          selectName="deploymentEnvironment"
          label={formatMessage(messages.deploymentMetrics)}
          data-testid="deployment-metrics-select-label"
        />
      ),
    },
    {
      type: Cards.PipelinesMetricCard,
      content: (
        <AverageDeploymentFrequency
          environmentType={environment?.type}
          {...deploymentFrequency}
        />
      ),
    },
    {
      type: Cards.PipelinesMetricCard,
      content: <AverageDeploymentFailureRate {...deploymentFailureRate} />,
    },
  ];

  return (
    <SidebarKeyboardShortcuts
      sidebarType={SIDEBAR_TYPE}
      toggleSidebar={toggleSidebar}
    >
      <Sidebar
        expandedWidth={expandedWidth}
        collapsedContent={[]}
        expandedContent={isJiraConnected ? expandedContent : noJiraContent}
        isCollapsed={isCollapsed}
        onResize={onResize}
      />
    </SidebarKeyboardShortcuts>
  );
};

export const PipelineListSidebar = memo(BasePipelineListSidebar);
